import UnitImage from "common/src/tft-static/UnitImage";
import * as Sd from "common/src/tft-static/Sd";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import * as React from "react";
import { createPortal } from "react-dom";
import * as Base64 from "../bindings/Base64";
import * as T from "common/src/bindings/Translation2";
import { Button as ShareButton } from "../common/Share.bs.js";
import { Button } from "@mui/material";
import { Clear } from "@mui/icons-material";
import Draggable from "./Draggable";
import TierContainer from "./TierContainer";
import TlNav from "./TlNav";
import {
  TierList,
  allTiers,
  transform,
  useTierList,
  emptyTierList,
  UNIT_SIZE,
  ITEM_CLASS,
} from "./TlCommon";
import { make as UnitTooltip } from "common/src/tft-static/UnitTooltip.bs.js";
import { make as SearchField } from "../components/SearchField2.bs.js";
import * as Search from "../common/Search2";
import AdLayout from "../app-core/AdLayout";
import {
  Provider as TftSetProvider,
  fromNum,
  use as useSet,
} from "common/src/data/TftSet.bs.js";
import TlSetSelect from "./TlSetSelect";

const sets: { [key: string]: number } = {
  A: 95,
  B: 10,
  C: 110,
  D: 120,
  E: 130,
  F: 140,
  G: 150,
};

const revSets: { [key: number]: string } = {
  "95": "A",
  "10": "B",
  "110": "C",
  "120": "D",
  "130": "E",
  "140": "F",
  "150": "G",
};

const encodeUrl = (tierlist: TierList, set: number): string => {
  const allUnits = Sd.allUnits(set);
  let encoding = revSets[set];
  for (const tier of allTiers) {
    for (let i = 0; i < 50; i++) {
      if (tierlist[tier].length <= i * 8) {
        break;
      }
      const units = tierlist[tier].slice(i * 8, i * 8 + 8);
      const header = Base64.encode(
        (allTiers.indexOf(tier) << 3) | (units.length - 1),
        1,
      );
      const encodedUnits = units
        .map((unitId) => Base64.encode(allUnits.indexOf(unitId), 1))
        .join("");
      encoding += header + encodedUnits;
    }
  }

  return encoding;
};

const decodeUrl = (qs: string): [TierList, number] => {
  const set = sets[qs[0]];
  const allUnits = Sd.allUnits(set);
  const tierlist: TierList = emptyTierList();

  if (qs.length < 2) {
    return [tierlist, 140];
  }

  let currIdx = 1;
  let currTier = "";
  let currTierLen = 0;
  let currTierIdx = 0;
  while (qs[currIdx]) {
    const curr = qs[currIdx];
    if (currTierIdx >= currTierLen) {
      const val = Base64.decode(curr);
      const count = (val & 0x7) + 1;
      const tier = allTiers[val >> 3];
      currTier = tier;
      currTierLen = count;
      currTierIdx = 0;
    } else {
      const val = Base64.decode(curr);
      const unitId = allUnits[val];
      tierlist[currTier].push(unitId);
      currTierIdx += 1;
    }

    currIdx += 1;
  }

  return [tierlist, set];
};

const Tier = React.memo(
  ({
    label,
    units,
    draggedId,
    searchMatch,
    onRemove,
  }: {
    label: string;
    units: Array<string>;
    draggedId: string | null;
    searchMatch: (unitId: string) => boolean;
    onRemove: (unitId: string) => void;
  }) => {
    return (
      <TierContainer label={label} itemCount={units.length}>
        {(dropIdx, dragStarted) => (
          <>
            {units
              .filter((unitId) => !dragStarted || unitId !== draggedId)
              .map((unitId, idx) => {
                const idx2 = idx + (idx >= dropIdx ? 1 : 0);
                return (
                  <div
                    key={unitId}
                    onClick={() => onRemove(unitId)}
                    style={{
                      transform: transform(idx2),
                    }}
                    className={`absolute transition-transform ${ITEM_CLASS} no-webkit-preview ${
                      searchMatch(unitId) ? "" : "opacity-70"
                    }`}
                  >
                    <Draggable key={unitId} id={unitId}>
                      <UnitTooltip unit={unitId}>
                        <UnitImage
                          unitId={unitId}
                          size={UNIT_SIZE}
                          className={`select-none no-webkit-preview`}
                          showName={false}
                        />
                      </UnitTooltip>
                    </Draggable>
                    <div className="text-xs leading-[1.1] text-center pt-[4px] truncate-2">
                      {T.units(unitId)}
                    </div>
                  </div>
                );
              })}
            {dropIdx === 99 || draggedId == null ? null : (
              <div
                style={{
                  transform: transform(
                    Math.min(
                      dropIdx,
                      units.length - (units.includes(draggedId) ? 1 : 0),
                    ),
                  ),
                }}
                className={`absolute transition-transform ${ITEM_CLASS}`}
              >
                <UnitImage
                  unitId={draggedId}
                  size={UNIT_SIZE}
                  className="select-none no-webkit-preview opacity-50"
                  showName={false}
                />
              </div>
            )}
          </>
        )}
      </TierContainer>
    );
  },
);

type Props = {
  set: number;
  setSet: (set: number) => void;
};

function UnitsTierListInner({ set, setSet }: Props) {
  const [search, setSearch] = React.useState("");
  const {
    clearTierList,
    draggedId,
    handleDragEnd,
    handleDragStart,
    shareUrl,
    tiers,
    tierItems,
    onClick,
    sensors,
    onRemove,
  } = useTierList({
    baseUrl: "units",
    encodeUrl,
    decodeUrl,
    set,
  });

  const searchMatch = React.useMemo(() => {
    if (search === "") {
      return () => true;
    } else {
      const searchIds = Search.searchUnits(search, set);
      return (id: string) => searchIds.has(id);
    }
  }, [search, set]);

  const remainingUnits = React.useMemo(
    () =>
      Sd.allUnits(set).filter(
        (unitId) => !tierItems.includes(unitId) && searchMatch(unitId),
      ),
    [tierItems, searchMatch, set],
  );

  return (
    <AdLayout>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <TlNav active="Units" />
        <div className="bg-bg flex flex-col rounded w-full md:w-[900px] p-4">
          <div className="flex items-center gap-6 h-10 mb-3">
            <h2 className="h2">{T.common("Units")}</h2>
            <Button
              disabled={tierItems.length === 0}
              size="small"
              startIcon={<Clear />}
              onClick={clearTierList}
              variant="outlined"
              color="primary"
            >
              {T.common("Clear")}
            </Button>
            <ShareButton.make forceFull url={shareUrl} />
            <div className="ml-auto">
              <TlSetSelect set={set} setSet={setSet} />
            </div>
          </div>
          <Tier
            label="S"
            units={tiers["S"]}
            draggedId={draggedId}
            searchMatch={searchMatch}
            onRemove={onRemove}
          />
          <Tier
            label="A"
            units={tiers["A"]}
            draggedId={draggedId}
            searchMatch={searchMatch}
            onRemove={onRemove}
          />
          <Tier
            label="B"
            units={tiers["B"]}
            draggedId={draggedId}
            searchMatch={searchMatch}
            onRemove={onRemove}
          />
          <Tier
            label="C"
            units={tiers["C"]}
            draggedId={draggedId}
            searchMatch={searchMatch}
            onRemove={onRemove}
          />
          <Tier
            label="D"
            units={tiers["D"]}
            draggedId={draggedId}
            searchMatch={searchMatch}
            onRemove={onRemove}
          />
          <div className="pt-4 pl-2">
            <SearchField search={search} setSearch={setSearch} />
          </div>
          <div className="flex flex-col gap-3 pt-4">
            {Array.from({ length: 5 }).map((_, idx) => {
              const tierUnits = remainingUnits.filter(
                (unitId) => Sd.unitTier(unitId) === idx + 1,
              );
              if (tierUnits.length == 0) {
                return null;
              }
              return (
                <div key={idx} className="flex flex-wrap gap-[10px]">
                  {tierUnits.map((unitId) => (
                    <div
                      key={unitId}
                      className={`${ITEM_CLASS} no-webkit-preview`}
                      onClick={() => onClick(unitId)}
                    >
                      {unitId === draggedId ? (
                        <UnitImage
                          unitId={unitId}
                          size={UNIT_SIZE}
                          className="select-none no-webkit-preview opacity-40"
                          showName={false}
                        />
                      ) : (
                        <Draggable id={unitId}>
                          <UnitTooltip unit={unitId}>
                            <UnitImage
                              unitId={unitId}
                              size={UNIT_SIZE}
                              className="select-none no-webkit-preview"
                              showName={false}
                            />
                          </UnitTooltip>
                        </Draggable>
                      )}
                      <div className="text-xs leading-[1.1] text-center pt-[4px] truncate-2">
                        {T.units(unitId)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {createPortal(
            <DragOverlay>
              {draggedId ? (
                <div className="elevation-8 cursor-grabbing">
                  <UnitImage
                    className="select-none no-webkit-preview"
                    unitId={draggedId}
                    size={UNIT_SIZE}
                    showName={false}
                  />
                </div>
              ) : null}
            </DragOverlay>,
            document.body,
          )}
        </div>
      </DndContext>
    </AdLayout>
  );
}

export default function UnitsTierList() {
  const [set, setSet] = React.useState(() => {
    const parts = window.location.href.split("/");
    const last = parts[parts.length - 1];
    return sets[last[0]] || 150;
  });
  return (
    <TftSetProvider.make value={fromNum(set)}>
      <UnitsTierListInner set={set} setSet={setSet} />
    </TftSetProvider.make>
  );
}
